import { Button } from 'components/atoms';
import {
  FormInput,
  InfoBox,
  LabeledStatus,
  TabsNavigation,
} from 'components/molecules';
import Popup from 'components/molecules/popup';
import { TopBar } from 'components/organisms';
import ExchangeRates from 'components/organisms/forms/statements/tabs/ExchangeRates';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import { useParamId } from 'hooks/useParamId';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PiDownload, PiTrash } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import {
  useDeleteStatement,
  useGetStatementRowsInfinite,
  useGetStatementSummaryRowsInfinite,
  useGetStatementUnrelatedRowsInfinite,
} from 'services/statements';
import {
  CURRENCY_ID,
  IStatement,
  STATEMENT_STATUS,
} from 'services/statements/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import { handleDownloadFile } from 'utils/global/linkToUrl.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { IFormProps } from '../types';
import Errors from './tabs/Errors';
import Rows from './tabs/Rows';
import SummaryRows from './tabs/SummaryRows';
import UnrelatedRows from './tabs/UnrelatedRows';

export enum statementsTabs {
  exchangeRates,
  rows,
  unrelatedRows,
  summaryRows,
  errors,
}

export const initialStatement: IStatement = {
  id: '',
  salesChannelId: '',
  settlementPeriodId: '',
  providerId: '',
  providerName: '',
  statementStatus: STATEMENT_STATUS.unknown,
  defaultCurrency: CURRENCY_ID.PLN,
  errors: [],
  defaultTotalValue: 0,
  statementFileId: '',
  statementFilePath: '',
  settlementPeriodName: { name: '', year: -1 },
  salesChannelName: '',
  exchangeRates: [
    {
      currencyId: 0,
      currency: {
        currencyCode: '',
        name: '',
        id: 0,
      },
      rate: 0,
      exchangeRateDate: '',
      tableCode: '',
    },
  ],
};

interface ExchangeRatesFormProps
  extends FormikConfig<IStatement>,
    IFormProps,
    II18nextPrefix {}

interface ExchangeRatesFormComponent extends React.FC<ExchangeRatesFormProps> {
  getRouterTabs?: (
    disabledChildren?: string[]
  ) => { path: string; element: JSX.Element }[];
}

const ExchangeRatesForm: ExchangeRatesFormComponent = ({
  i18nextPrefix = '',
  initialValues = initialStatement,
  tittle = '',
  onSubmit = () => {},
  status,
}) => {
  const navigate = useNavigate();

  const id = useParamId('statements');

  const { t } = useTranslation();

  const rows = useGetStatementRowsInfinite(id);

  const unrelatedRows = useGetStatementUnrelatedRowsInfinite(id);

  const summaryRows = useGetStatementSummaryRowsInfinite(id);

  const deleteStatementMutation = useDeleteStatement();

  const deleteStatement = async (id: string) => {
    await deleteStatementMutation.mutateAsync(id, {
      onSuccess: () => {
        navigate('/statements');
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      initialStatus={status}
      onSubmit={onSubmit}
      validateOnChange={true}
      validateOnBlur={true}>
      {({ values }) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              title={getSettlementPeriodName(values.settlementPeriodName)}
              topbarContent={
                <div className='flex gap-4 items-center'>
                  <LabeledStatus.Statement status={values.statementStatus} />
                  <div className='flex'>
                    <Button
                      onlyIcon
                      isClear
                      onClick={() =>
                        handleDownloadFile(initialValues.statementFilePath)
                      }>
                      <Button.Icon icon={<PiDownload />} />
                    </Button>
                    <Popup.DeleteWarning
                      name={`${getSettlementPeriodName(
                        values.settlementPeriodName
                      )} ${values.salesChannelName} ${values.providerName}`}
                      onRemove={() => {
                        deleteStatement(id);
                      }}
                      trigger={
                        <Button
                          variant={BUTTON_VARIANT.remove}
                          onlyIcon
                          isClear>
                          <Button.Icon icon={<PiTrash />} />
                        </Button>
                      }
                    />
                  </div>
                </div>
              }>
              <InfoBox>
                <InfoBox.Items columns={1}>
                  <FormInput.Text
                    name='salesChannelName'
                    label={`${t('input.label.salesChannel')}`}
                  />
                  <FormInput.Text
                    name='providerName'
                    label={`${t('input.label.provider')}`}
                  />

                  <FormInput.Number
                    name='defaultTotalValue'
                    label={`${t('input.label.totalValue')}`}
                    suffix={` ${CURRENCY_ID[values.defaultCurrency]}`}
                  />
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  {
                    value: statementsTabs.exchangeRates,
                    name: 'exchangeRate',
                  },
                  ...(rows.data?.pages.flat().length
                    ? [
                        {
                          value: statementsTabs.rows,
                          name: 'rows',
                        },
                      ]
                    : []),
                  ...(summaryRows.data?.pages.flat().length
                    ? [
                        {
                          value: statementsTabs.summaryRows,
                          name: 'summaryRows',
                        },
                      ]
                    : []),
                  ...(unrelatedRows.data?.pages.flat().length
                    ? [
                        {
                          value: statementsTabs.unrelatedRows,
                          name: 'unrelatedRows',
                        },
                      ]
                    : []),
                  ...(values.errors.length
                    ? [
                        {
                          value: statementsTabs.errors,
                          name: 'errors',
                        },
                      ]
                    : []),
                ]}
                i18nextPrefix={'statements'}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

ExchangeRatesForm.getRouterTabs = (disabledChildren: string[] = []) =>
  [
    { path: 'exchangeRate', element: <ExchangeRates /> },
    { path: 'unrelatedRows', element: <UnrelatedRows /> },
    { path: 'summaryRows', element: <SummaryRows /> },
    { path: 'rows', element: <Rows /> },
    { path: 'errors', element: <Errors /> },
  ].filter(child => !disabledChildren.includes(child.path));

export default ExchangeRatesForm;
